import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { itemService, categoryService } from '../services/api';

export interface Item {
  id: number;
  name: string;
  description: string;
  price: number;
  isBought: boolean;
  purchaseLink: string;
  photo?: {
    url: string;
  };
  category?: {
    name: string;
  };
}

export interface Category {
  id: number;
  name: string;
  description?: string;
}

// Query key factories
const itemKeys = {
  all: ['items'] as const,
  list: () => [...itemKeys.all, 'list'] as const,
};

const categoryKeys = {
  all: ['categories'] as const,
  list: () => [...categoryKeys.all, 'list'] as const,
};

/**
 * Fetch all items with categories
 * Uses React Query caching with 5-minute stale time
 */
export const useItems = () => {
  return useQuery({
    queryKey: itemKeys.list(),
    queryFn: async () => {
      const response = await itemService.getAll();
      return response.data || [];
    },
  });
};

/**
 * Mark an item as bought (mutation)
 * Automatically invalidates the items query after success
 */
export const useMarkAsBought = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, password }: { itemId: number; password: string }) =>
      itemService.markAsBought(itemId, password),
    onSuccess: () => {
      // Invalidate items query to refetch fresh data
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Fetch all categories
 */
export const useCategories = () => {
  return useQuery({
    queryKey: categoryKeys.list(),
    queryFn: async () => {
      const response = await categoryService.getAll();
      return response.data || [];
    },
  });
};

/**
 * Create a new item (admin)
 */
export const useCreateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => itemService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Update an item (admin)
 */
export const useUpdateItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      itemService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Delete an item (admin)
 */
export const useDeleteItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => itemService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Create a new category (admin)
 */
export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => categoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.list() });
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Update a category (admin)
 */
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      categoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.list() });
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};

/**
 * Delete a category (admin)
 */
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.list() });
      queryClient.invalidateQueries({ queryKey: itemKeys.list() });
    },
  });
};
